import { useEffect, useMemo, useRef } from "react";
import { useReactFlow, useStoreApi } from "@xyflow/react";
import { OwnershipFlowEdge, OwnershipFlowNode } from "../ownership.types";
import {
    getOwnershipFitViewport,
    getOwnershipGraphBounds,
} from "./ownershipFitView.helpers";

export function useOwnershipFitView({
    nodes,
    edges,
    isFlowReady,
}: {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
    isFlowReady: boolean;
}): void {
    const store = useStoreApi();
    const { setViewport } = useReactFlow();
    const graphStructureKey = useMemo(
        () =>
            JSON.stringify({
                nodes: nodes.map((node) => [
                    node.id,
                    node.type,
                    node.parentId ?? null,
                ]),
                edges: edges.map((edge) => [
                    edge.id,
                    edge.source,
                    edge.target,
                    edge.data?.relationship.type ?? null,
                ]),
            }),
        [nodes, edges],
    );
    const lastFitStructureRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isFlowReady) {
            return;
        }
        if (nodes.length === 0) {
            lastFitStructureRef.current = null;
            return;
        }
        if (lastFitStructureRef.current === graphStructureKey) {
            return;
        }
        const bounds = getOwnershipGraphBounds(nodes);
        if (!bounds) {
            lastFitStructureRef.current = null;
            return;
        }
        const { width, height, minZoom } = store.getState();
        if (width === 0 || height === 0) {
            return;
        }
        lastFitStructureRef.current = graphStructureKey;
        void setViewport(
            getOwnershipFitViewport({ bounds, width, height, minZoom }),
        );
    }, [graphStructureKey, isFlowReady, nodes, setViewport, store]);
}
